import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SystemSettings {
  site_name: string;
  site_description: string;
  maintenance_mode: boolean;
  user_registration: boolean;
  max_upload_size: number;
  allowed_file_types: string[];
  content_filters: {
    profanity: boolean;
    spam: boolean;
    sensitive_content: boolean;
  };
  notification_settings: {
    email_notifications: boolean;
    push_notifications: boolean;
  };
}

export function SystemSettings() {
  const [settings, setSettings] = useState<SystemSettings>({
    site_name: 'RoyaltySuite',
    site_description: 'A social platform for distinguished individuals',
    maintenance_mode: false,
    user_registration: true,
    max_upload_size: 100,
    allowed_file_types: ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'],
    content_filters: {
      profanity: true,
      spam: true,
      sensitive_content: true
    },
    notification_settings: {
      email_notifications: true,
      push_notifications: true
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('settings')
        .single();

      if (error) throw error;
      if (data) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const { error } = await supabase
        .rpc('update_system_settings', {
          new_settings: settings
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 text-royal-purple animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-paper rounded-xl shadow-sm">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">System Settings</h2>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Basic Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Basic Settings</h3>
              
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Site Name
                </label>
                <input
                  type="text"
                  value={settings.site_name}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    site_name: e.target.value
                  }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Site Description
                </label>
                <textarea
                  value={settings.site_description}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    site_description: e.target.value
                  }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.maintenance_mode}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      maintenance_mode: e.target.checked
                    }))}
                    className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Maintenance Mode
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.user_registration}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      user_registration: e.target.checked
                    }))}
                    className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Allow User Registration
                  </span>
                </label>
              </div>
            </div>

            {/* Upload Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Upload Settings</h3>
              
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Max Upload Size (MB)
                </label>
                <input
                  type="number"
                  value={settings.max_upload_size}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    max_upload_size: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Allowed File Types
                </label>
                <div className="space-y-2">
                  {['image/jpeg', 'image/png', 'image/gif', 'video/mp4'].map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-elevated p-2 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        checked={settings.allowed_file_types.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSettings(prev => ({
                              ...prev,
                              allowed_file_types: [...prev.allowed_file_types, type]
                            }));
                          } else {
                            setSettings(prev => ({
                              ...prev,
                              allowed_file_types: prev.allowed_file_types.filter(t => t !== type)
                            }));
                          }
                        }}
                        className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Content Filters */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Content Filters</h3>
              
              <div className="space-y-2">
                {Object.entries(settings.content_filters).map(([key, value]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-elevated p-2 rounded-lg transition-colors">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        content_filters: {
                          ...prev.content_filters,
                          [key]: e.target.checked
                        }
                      }))}
                      className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {key.split('_').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Notification Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Notification Settings</h3>
              
              <div className="space-y-2">
                {Object.entries(settings.notification_settings).map(([key, value]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-elevated p-2 rounded-lg transition-colors">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        notification_settings: {
                          ...prev.notification_settings,
                          [key]: e.target.checked
                        }
                      }))}
                      className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {key.split('_').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-dark-elevated border-t dark:border-dark-border">
          <button
            onClick={handleSave}
            disabled={saving}
            className={cn(
              "px-4 py-2 rounded-lg font-medium transition-colors",
              saving
                ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                : "bg-royal-purple text-white hover:bg-royal-purple/90"
            )}
          >
            {saving ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </div>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}